<template>
  <div class="exhibit-wrap">
    <header class="exhibit-header">
      <span class="exhibit-title"
        >Kingdee Cloud Building
        <span class="exhibit-subtitle">Art Space</span></span
      >
      <span class="exhibit-menu" @click="navToExhibitList">查看更多</span>
    </header>
    <div class="exhibit-swiper" v-if="mediaList && mediaList.length > 0">
      <swiper
        :modules="modules"
        :slides-per-view="1"
        :space-between="0"
        :pagination="{ clickable: true }"
        class="exhibit-swiper-container"
        @swiper="onSwiper"
        @slideChange="onSlideChange"
      >
        <swiper-slide v-for="(item, index) in mediaList" :key="index">
          <div class="swiper-media-wrapper">
            <img
              v-if="isImage(item)"
              :src="getImageUrl(item)"
              :alt="`Exhibit ${index + 1}`"
              class="swiper-media"
            />
            <video
              v-else-if="isVideo(item)"
              :src="getMediaUrl(item)"
              class="swiper-media"
              controls
              preload="metadata"
              playsinline
              webkit-playsinline
              x5-playsinline
            ></video>
          </div>
        </swiper-slide>
      </swiper>
    </div>
    <section class="exhibit-info">
      <div class="exhibit-author">
        <h2>
          {{ exhibitDetail.name_cn }}
          <span class="en">{{ exhibitDetail.name_en }}</span>
        </h2>
        <div
          class="artist"
          @click="navToAuthor(value)"
          v-for="value in exhibitDetail.artist"
          :key="value._id || value.id"
        >
          <div class="artist-row">
            <!-- 作者名字 -->
            <span class="artist-name">
              {{ value.name_cn }} {{ value.name_en }}
            </span>

            <!-- 查看详情 -->
            <span class="artist-tip">
              <span class="arrow">←</span>
              查看作者详情
            </span>
          </div>
        </div>
        <div class="desc">
          {{ exhibitDetail.little_title }}
        </div>
      </div>
      <div class="exhibit-record" v-if="exhibitDetail.ex_record">
        <div class="record-title">参展记录</div>
        <div
          class="record-item"
          v-html="exhibitDetail.ex_record.replace(/\n/g, '<br />')"
        ></div>
      </div>
      <div class="exhibit-desc">
        {{ exhibitDetail.intro_cn }}
      </div>
      <div class="exhibit-desc">
        {{ exhibitDetail.intro_en }}
      </div>
    </section>
    <Footer
      :is-liked="isLiked"
      :like-count="exhibitDetail.likeCount"
      :share-count="exhibitDetail.shareCount"
      @onLike="handleLike"
    />
  </div>
</template>

<script setup>
import Footer from "@/components/Footer.vue";
import router from "@/router";
import {
  getExhibitDetail,
  recordVisit,
  recordShare,
  getLikeStatus,
  doLike,
  doUnlike,
} from "@/service/api";
import { onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper/modules";
import { useWxShare, wxAuth } from "@/hooks/useWechatAuth";
import "swiper/css";
import "swiper/css/pagination";
import { showFailToast, showToast } from "vant";

const modules = [Pagination];

const exhibitDetail = ref({});
const route = useRoute();

const navToExhibitList = () => {
  router.push("/exhibitList");
};

// 合并图片和视频列表
const mediaList = computed(() => {
  const images = exhibitDetail.value.ex_images || [];
  const videos = exhibitDetail.value.ex_videos || [];
  return [...images, ...videos];
});

// 判断是否为图片
const isImage = (item) => {
  if (typeof item === "string") {
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(item);
  }
  if (item && item.type) {
    return item.type === "image";
  }
  return true; // 默认为图片
};

// 判断是否为视频
const isVideo = (item) => {
  if (typeof item === "string") {
    return /\.(mp4|webm|ogg|mov)$/i.test(item);
  }
  if (item && item.type) {
    return item.type === "video";
  }
  return false;
};
// 获取图片URL
const getImageUrl = (item) => {
  if (typeof item === "string") {
    return item;
  }
  if (item && item.url) {
    return item.url;
  }
  return item;
};

// 获取媒体URL
const getMediaUrl = (item) => {
  if (typeof item === "string") {
    return videoUrltoStreamUrl(item);
  }
  if (item && item.url) {
    return videoUrltoStreamUrl(item.url);
  }
  return videoUrltoStreamUrl(item);
};

const videoUrltoStreamUrl = (url) => {
  if (!url) return "";

  // 已经是 stream 接口的，直接返回
  if (url.includes("/jd-art/api/medias/stream/")) {
    return url;
  }

  // 只处理 uploads/media 下的视频
  const match = url.match(/\/uploads\/media\/(.+)$/);
  if (!match) return url;

  const filename = match[1];
  return `/jd-art/api/medias/stream/${filename}`;
};

const isLiked = ref(false);
const getLikeInfo = () => {
  getLikeStatus({
    targetId: route.query.id,
    targetType: "exhibit",
  }).then((res) => {
    isLiked.value = res.liked;
  });
};

onMounted(async () => {
  await wxAuth();
  const res = await getExhibitDetail(route.query.id);
  exhibitDetail.value = res.exhibit || {};

  recordVisit("exhibit", route.query.id);

  getLikeInfo();

  handleShare();
});

const navToAuthor = (artist) => {
  router.push(`/author?id=${artist._id}`);
};

const handleLike = (flag) => {
  (flag ? doLike : doUnlike)({
    targetId: route.query.id,
    targetType: "exhibit",
  })
    .then(() => {
      getLikeInfo();
      flag ? exhibitDetail.value.likeCount++ : exhibitDetail.value.likeCount--;
    })
    .catch((err) => {
      showFailToast(err);
    });
};

const handleShare = () => {
  useWxShare({
    title: "金蝶艺术馆",
    desc: exhibitDetail.value.name_cn,
    link: `${window.location.origin}${window.location.pathname}/#/exhibit?id=${route.query.id}`,
    imgUrl: exhibitDetail.value.ex_images[0]
      ? getImageUrl(exhibitDetail.value.ex_images[0])
      : "",
    success: async () => {
      // showToast("分享成功");
      const res = await recordShare("exhibit", route.query.id);
      if (res.message === "ok") {
        exhibitDetail.value.shareCount++;
      }
    },
  });
};

const swiperInstance = ref(null);

const onSwiper = (swiper) => {
  swiperInstance.value = swiper;
};

const pauseAllVideos = () => {
  const videos = document.querySelectorAll(".exhibit-swiper video");
  videos.forEach((video) => {
    if (!video.paused) {
      video.pause();
      video.currentTime = 0; // 是否重置，看你需求
    }
  });
};

const onSlideChange = () => {
  pauseAllVideos();
};
</script>

<style scoped lang="scss">
.exhibit-wrap {
  background: #fff;
  min-height: 100vh;
  font-family: "PingFang SC", "Helvetica Neue", Arial, sans-serif;
  color: #222;
  padding: 40px 24px 90px;
}

.exhibit-header {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  font-size: 12px;
  background: #fff;

  .exhibit-title {
    font-weight: 500;

    .exhibit-subtitle {
      color: #bdbdbd;
      margin-left: 4px;
    }
  }

  .exhibit-menu {
    font-size: 12px;
    color: #222;
    letter-spacing: 2px;
  }
}

.exhibit-swiper {
  width: 100%;
  margin: 24px auto 0 auto;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  .exhibit-swiper-container {
    width: 100%;
    border-radius: 6px;
  }

  .swiper-media-wrapper {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
  }

  .swiper-media {
    width: 100%;
    height: 200px;
    display: block;
  }

  // Swiper 分页器样式
  :deep(.swiper-pagination) {
    bottom: 10px;

    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      background: rgba(255, 255, 255, 0.8);
      opacity: 0.5;

      &.swiper-pagination-bullet-active {
        opacity: 1;
        background: #fff;
      }
    }
  }
}

.exhibit-info {
  margin: 36px auto 0 auto;

  .exhibit-author {
    border-left: 4px solid #222;
    padding: 16px 18px;

    h2 {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 12px;

      .en {
        font-size: 18px;
        color: #444;
        margin-left: 8px;
        font-weight: normal;
      }
    }

    .artist {
      margin-bottom: 12px;
      cursor: pointer;
    }

    .artist-row {
      display: inline-flex; // 不占满整行
      align-items: baseline;
      gap: 10px; // 控制名字和提示之间距离
    }

    /* 作者名 */

    // .artist-name {
    //   font-size: 18px;
    //   color: #9a9a9a;
    //   text-decoration: underline;
    // }

    .artist-name {
      display: inline-block;
      font-size: 4.8vw;
      color: #9a9a9a;
      line-height: 1.2;
      padding-bottom: 1px;
      border-bottom: 1.5px solid #9a9a9a;
    }

    /* 查看作者 */

    .artist-tip {
      font-size: 13px;
      color: #9a9a9a;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .arrow {
      font-size: 13px;
      transition: transform 0.2s ease;
    }

    .artist:hover .arrow {
      transform: translateX(-3px);
    }

    .artist:hover .artist-tip {
      color: #666;
    }

    .desc {
      font-size: 15px;
      color: #666;
      margin-bottom: 18px;
      white-space: pre-line;
    }
  }

  .exhibit-record {
    border-left: 1px solid #222;
    padding: 16px 18px;
    margin-bottom: 18px;

    .record-title {
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 16px;
    }

    .record-item {
      font-size: 14px;
      color: #444;
      margin-bottom: 10px;
      line-height: 1.6;
    }
  }

  .exhibit-desc {
    font-size: 15px;
    color: #333;
    line-height: 1.8;
    margin-top: 18px;
    white-space: pre-line;
  }
}
</style>
